import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { container } from "tsyringe";
import { AnalyticsService } from "../../services/analytics";

class PdfDocument {
  objectUrl: string | undefined;
  name: string | undefined;
}

export interface PdfMergerState {
  pdfDocumentsToMerge: PdfDocument[];
}

const initialState: PdfMergerState = {
  pdfDocumentsToMerge: [],
};

export const PdfMergerSlice = createSlice({
  name: "pdfMerger",
  initialState,
  reducers: {
    removeDocument: (state, action: PayloadAction<PdfDocument>) => {
      state.pdfDocumentsToMerge = state.pdfDocumentsToMerge.filter(
        (doc) => doc.name !== action.payload.name
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addPdfAsync.fulfilled, (state, action) => {
      if (action.payload === undefined) {
        return;
      }

      state.pdfDocumentsToMerge.push({
        name: action.payload.name,
        objectUrl: action.payload.objectUrl,
      });
    });
  },
});

export const addPdfAsync = createAsyncThunk(
  "pdfMerger/addPdfAsync",
  async (file: File | undefined) => {
    const analyticsService = container.resolve(AnalyticsService);
    analyticsService.LogEvent({
      category: "Button Click",
      action: "Convert Clicked",
      label: "PDF Merger",
    });
    if (file === undefined) return;

    var objectUrl = URL.createObjectURL(file);
    return {
      name: file.name,
      objectUrl: objectUrl,
    };
  }
);

export const mergePdfDocumentsAsync = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("pdfMerger/mergePdfDocumentsAsync", async (_, { getState }) => {
  const state = getState();

  const PDFMergerModule = await import("pdf-merger-js");
  const PDFMerger = PDFMergerModule.default;
  const merger = new PDFMerger();

  for (const document of state.pdfMerger.pdfDocumentsToMerge) {
    if (document.objectUrl) {
      await merger.add(document.objectUrl);
    }
  }

  const buffer = await merger.saveAsBuffer();

  // Convert buffer to Uint8Array
  const uint8Array = new Uint8Array(buffer);

  // Create Blob from Uint8Array
  const blob = new Blob([uint8Array], { type: "application/pdf" });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  document.body.appendChild(a);
  a.href = url;
  a.download = "merged.pdf";
  a.click();
});

export const { removeDocument } = PdfMergerSlice.actions;

export const selectState = (state: RootState) => state.pdfMerger;

export default PdfMergerSlice.reducer;
